/**
 * Cookie names used by auth system
 * Shared between Server and Client JWT managers
 */
export const COOKIE_NAMES = {
  JWT: 'pixum-api-jwt',
  REFRESH_TOKEN: 'refreshToken',
  LAST_REFRESH_TIME: 'pixum-api-jwt-lastRefreshTime',
  UUID: 'pixum-api-uuid',
  DOWNLOAD_DIALOG_SHOWN: 'downloadDialogShown',
  CANCELED_ARTICLES: 'pixum-canceled-articles',
  EXPRESS_DESIGNER_SELECTED_IMAGE: 'express-designer-selected-image',
} as const;

/**
 * SameSite cookie attribute values for client-side cookies
 * Controls when cookies are sent with cross-site requests
 * Values match CookieOptions type: 'None' | 'Lax' | 'Strict'
 */
export const CLIENT_COOKIE_SAME_SITE = {
  /** Cookie sent with same-site and cross-site requests (least restrictive) */
  NONE: 'None' as const,
  /** Cookie sent only with top-level navigations and same-site requests (default, balanced security) */
  LAX: 'Lax' as const,
  /** Cookie sent only with same-site requests (most restrictive, highest security) */
  STRICT: 'Strict' as const,
} as const;

/**
 * SameSite cookie attribute values for server-side cookies (Next.js)
 * Uses lowercase values as required by Next.js cookies().set()
 */
export const SERVER_COOKIE_SAME_SITE = {
  /** Cookie sent with same-site and cross-site requests (least restrictive) */
  NONE: 'none' as const,
  /** Cookie sent only with top-level navigations and same-site requests (default, balanced security) */
  LAX: 'lax' as const,
  /** Cookie sent only with same-site requests (most restrictive, highest security) */
  STRICT: 'strict' as const,
} as const;

/**
 * Cookie configuration for client-side cookies
 * Only includes cookies that are set from client-side code
 * Property names use camelCase to match CookieOptions interface
 * Can be spread directly into setCookie() function
 */
export const CLIENT_COOKIE_CONFIG = {
  DOWNLOAD_DIALOG_SHOWN: {
    maxAge: 60 * 60 * 24 * 365, // 1 year
    httpOnly: false, // Client needs to read
    secure: true,
    sameSite: CLIENT_COOKIE_SAME_SITE.LAX,
    path: '/',
  },
  // Same attributes as storeLastRefreshTimeInCookie in js-px-lib (editor): it outlives the JWT on purpose
  LAST_REFRESH_TIME: {
    maxAge: 60 * 60 * 24 * 28, // 4 weeks
    httpOnly: false,
    secure: true,
    sameSite: CLIENT_COOKIE_SAME_SITE.LAX,
    path: '/',
  },
} as const;

/**
 * Cookie configuration for server-side cookies (Next.js)
 * Uses lowercase sameSite values required by Next.js cookies().set()
 * Property names use camelCase to match Next.js ResponseCookie interface
 */
export const SERVER_COOKIE_CONFIG = {
  // Same attributes as storeLastRefreshTimeInCookie in js-px-lib (editor): it outlives the JWT on purpose
  LAST_REFRESH_TIME: {
    maxAge: 60 * 60 * 24 * 28, // 4 weeks
    httpOnly: false,
    secure: true,
    sameSite: SERVER_COOKIE_SAME_SITE.LAX,
    path: '/',
  },
  CANCELED_ARTICLES: {
    maxAge: 60 * 60 * 24 * 30, // 30 days — aligned with ELIGIBLE_WINDOW_DAYS in OrderTransformer
    httpOnly: true,
    secure: true,
    sameSite: SERVER_COOKIE_SAME_SITE.LAX,
    path: '/',
  },
} as const;
